// Tactical decision-support layers: self position, view fitting, routing,
// coverage heat, hot-spots and (NEW, additive) operational watch zones.
// Split out of DispatchMap.jsx (refactor — logic unchanged except added ZoneLayer).
import React, { useEffect, useRef, useState } from "react";
import { Marker, Polyline, Rectangle, Circle, CircleMarker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import client from "@/lib/api";
import { valid, coreFit } from "./mapShared";

// Tracks the live map centre/zoom so the Waze traffic view opens exactly where
// the operator is looking.
export function CenterTracker({ viewRef }) {
  const map = useMap();
  useEffect(() => { viewRef.current = { c: map.getCenter(), z: map.getZoom() }; }, [map, viewRef]);
  useMapEvents({ moveend: () => { viewRef.current = { c: map.getCenter(), z: map.getZoom() }; } });
  return null;
}

// "YOU ARE HERE" — the MDT user's own live fix; centers the map once on first fix.
const youIcon = L.divIcon({
  className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#22D3EE;border:3px solid #fff;box-shadow:0 0 0 6px rgba(34,211,238,.35);animation:pulse 1.6s infinite"></div>',
});
export function SelfLayer({ selfPos, headingUp = false }) {
  const map = useMap();
  const centered = useRef(false);
  useEffect(() => {
    if (selfPos && !centered.current) { centered.current = true; map.setView([selfPos.lat, selfPos.lng], 15); }
  }, [selfPos, map]);
  if (!selfPos || !valid(selfPos.lat, selfPos.lng)) return null;
  const hasHdg = Number.isFinite(selfPos.heading);
  const mph = Number.isFinite(selfPos.speed) ? Math.round(selfPos.speed * 2.23694) : null;
  const icon = hasHdg ? L.divIcon({
    className: "", iconSize: [30, 30], iconAnchor: [15, 15],
    html: `<div style="width:30px;height:30px;transform:rotate(${headingUp ? 0 : selfPos.heading}deg);transition:transform .4s;filter:drop-shadow(0 0 6px rgba(34,211,238,.8))">
      <svg viewBox="0 0 30 30" width="30" height="30"><polygon points="15,2 26,27 15,21 4,27" fill="#22D3EE" stroke="#fff" stroke-width="2" stroke-linejoin="round"/></svg></div>`,
  }) : youIcon;
  const label = `YOU ARE HERE${hasHdg ? ` · ${Math.round(selfPos.heading)}° ${cardinal(selfPos.heading)}` : ""}${mph != null ? ` · ${mph} MPH` : ""}`;
  return (
    <>
      {Number.isFinite(selfPos.accuracy) && selfPos.accuracy > 5 && (
        <CircleMarker center={[selfPos.lat, selfPos.lng]} radius={16}
          pathOptions={{ color: "#22D3EE", weight: 1, fillColor: "#22D3EE", fillOpacity: 0.12 }} />
      )}
      <Marker position={[selfPos.lat, selfPos.lng]} icon={icon} zIndexOffset={1200}>
        {!headingUp && (
          <Tooltip permanent direction="top" offset={[0, -14]}>
            <span data-testid="self-marker-label" style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 10 }}>{label}</span>
          </Tooltip>
        )}
      </Marker>
    </>
  );
}
const cardinal = (d) => ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(d / 45) % 8];

// Follow-me: keep the map centered on every new GPS fix; a manual drag releases it.
export function FollowMe({ selfPos, on, setOn }) {
  const map = useMap();
  useEffect(() => {
    if (on && selfPos && valid(selfPos.lat, selfPos.lng)) map.panTo([selfPos.lat, selfPos.lng], { animate: true, duration: 0.5 });
  }, [on, selfPos, map]);
  useMapEvents({ dragstart: () => { if (on) setOn(false); } });
  return null;
}

// Officer's own live route to their assigned call: re-routes when the fix moves >100 m,
// draws the path and reports {distance_m, duration_s} back for the nav HUD.
export function EtaToCall({ selfPos, target, onEta }) {
  const [route, setRoute] = useState(null);
  const lastFrom = useRef(null);
  const tLat = target?.location?.lat, tLng = target?.location?.lng, tId = target?.id;
  useEffect(() => {
    if (!selfPos || !valid(selfPos.lat, selfPos.lng) || !valid(tLat, tLng)) { setRoute(null); onEta(null); lastFrom.current = null; return; }
    const lf = lastFrom.current;
    if (lf && lf.id === tId && L.latLng(lf.lat, lf.lng).distanceTo([selfPos.lat, selfPos.lng]) < 100) return;
    lastFrom.current = { id: tId, lat: selfPos.lat, lng: selfPos.lng };
    let alive = true;
    client.get("/route", { params: { from_lat: selfPos.lat, from_lng: selfPos.lng, to_lat: tLat, to_lng: tLng } })
      .then(({ data }) => { if (!alive) return; setRoute(data); onEta({ distance_m: data.distance_m, duration_s: data.duration_s, fallback: data.fallback, cc: target.cc_number }); })
      .catch(() => { if (alive) { setRoute(null); onEta(null); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfPos?.lat, selfPos?.lng, tLat, tLng, tId]);
  if (!route?.coords?.length) return null;
  return <Polyline positions={route.coords} pathOptions={{ color: "#22D3EE", weight: 5, opacity: 0.9 }} />;
}

export function MapRefCapture({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

// Re-measure the map whenever its container resizes (fullscreen, panel toggles,
// orientation change) so tiles fill the whole area instead of leaving a grey band.
export function AutoResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const fix = () => map.invalidateSize({ animate: false });
    const ro = new ResizeObserver(fix);
    ro.observe(el);
    const t = setTimeout(fix, 300);
    window.addEventListener("orientationchange", fix);
    return () => { ro.disconnect(); clearTimeout(t); window.removeEventListener("orientationchange", fix); };
  }, [map]);
  return null;
}

// Fit the view ONCE, the first time real points arrive. After that the operator
// stays in control — background polling (every ~2s) must NOT snap the zoom/pan back.
export function Fit({ points }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || !points.length) return;
    fitted.current = true;
    if (window.__mapFocusPending) return;
    const core = coreFit(points);
    const b = L.latLngBounds(core);
    map.fitBounds(b.pad(0.3), { animate: false, maxZoom: 15 });
    if (map.getZoom() < 9) map.setView(b.getCenter(), 12, { animate: false });
  }, [points, map]);
  return null;
}

// "Who's closest" routing: for each open P1 call (and the selected call) draw the
// fastest road route from the nearest AVAILABLE/unassigned unit to the scene.
function nearestAvailableUnit(units, inc) {
  const il = inc.location;
  let best = null, bestD = Infinity;
  units.forEach((u) => {
    if (u.current_incident || u.status !== "available" || !valid(u.lat, u.lng)) return;
    const dx = u.lat - il.lat, dy = u.lng - il.lng, d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = u; }
  });
  return best;
}

export function RouteLayer({ units, incidents, selectedId }) {
  const [routes, setRoutes] = useState([]);
  const cache = useRef({});
  const targets = incidents.filter((i) => i.status !== "closed" && (i.priority === 1 || i.id === selectedId));
  const pairs = targets.map((inc) => ({ inc, u: nearestAvailableUnit(units, inc) })).filter((p) => p.u);
  const sig = pairs.map((p) => `${p.inc.id}:${p.inc.priority}:${p.inc.location.lat.toFixed(4)},${p.inc.location.lng.toFixed(4)}:${p.u.unit_id}:${p.u.lat.toFixed(4)},${p.u.lng.toFixed(4)}`).join("|");

  useEffect(() => {
    let alive = true;
    (async () => {
      const out = [];
      for (const { inc, u } of pairs) {
        const key = `${u.unit_id}:${u.lat.toFixed(4)},${u.lng.toFixed(4)}->${inc.location.lat.toFixed(4)},${inc.location.lng.toFixed(4)}`;
        let rec = cache.current[key];
        if (!rec) {
          try {
            const { data } = await client.get("/route", { params: { from_lat: u.lat, from_lng: u.lng, to_lat: inc.location.lat, to_lng: inc.location.lng } });
            rec = { coords: data.coords, distance_m: data.distance_m, duration_s: data.duration_s, fallback: data.fallback };
            cache.current[key] = rec;
          } catch { rec = null; }
        }
        if (rec) out.push({ id: inc.id, unit: u.unit_id, p1: inc.priority === 1, ...rec });
      }
      if (alive) setRoutes(out);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return routes.map((r) => (
    <Polyline key={r.id} positions={r.coords}
      pathOptions={{ color: r.p1 ? "#DC2626" : "#F59E0B", weight: 4, opacity: 0.85, dashArray: "8 8" }}>
      <Tooltip permanent direction="center" className="route-eta-tip">
        {r.unit} → {r.duration_s < 60 ? `${r.duration_s}s` : `${Math.round(r.duration_s / 60)} min`}{r.fallback ? " ~" : ""}
      </Tooltip>
    </Polyline>
  ));
}

// Response-coverage heat overlay. Samples a grid over the visible map and colours
// each cell by the drive-time (haversine @ 30 mph) from the NEAREST AVAILABLE unit,
// so supervisors instantly see gaps (>threshold minutes) and can reposition patrols.
const _KMH = 48.28; // 30 mph
function _haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371, toR = Math.PI / 180;
  const dLat = (lat2 - lat1) * toR, dLng = (lng2 - lng1) * toR;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toR) * Math.cos(lat2 * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
function coverageColor(min, threshold) {
  // green (well covered) -> amber -> red (gap). Ratio caps at 1.6x threshold.
  const ratio = Math.min(min / threshold, 1.6) / 1.6;
  const hue = 130 * (1 - ratio); // 130=green, 0=red
  return `hsl(${hue}, 82%, 45%)`;
}
export function CoverageLayer({ units, threshold, hotspots = [] }) {
  const map = useMap();
  const [, setTick] = useState(0);
  useMapEvents({ moveend: () => setTick((t) => t + 1), zoomend: () => setTick((t) => t + 1) });
  const avail = units.filter((u) => !u.current_incident && u.status === "available" && valid(u.lat, u.lng));
  const b = map.getBounds();
  const cols = 22, rows = 16;
  const dLat = (b.getNorth() - b.getSouth()) / rows;
  const dLng = (b.getEast() - b.getWest()) / cols;
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const s = b.getSouth() + r * dLat, w = b.getWest() + c * dLng;
      const clat = s + dLat / 2, clng = w + dLng / 2;
      let bestKm = Infinity;
      avail.forEach((u) => { const km = _haversineKm(clat, clng, u.lat, u.lng); if (km < bestKm) bestKm = km; });
      const min = avail.length ? (bestKm / _KMH) * 60 : 9999;
      // Historical demand weighting: cells containing 90-day hot-spots render more
      // opaque, so an uncovered hot-spot screams louder than an uncovered field.
      let demand = 0;
      hotspots.forEach((hs) => {
        if (hs.lat >= s && hs.lat < s + dLat && hs.lng >= w && hs.lng < w + dLng) demand += hs.weight;
      });
      cells.push({ id: `${r}-${c}`, bounds: [[s, w], [s + dLat, w + dLng]], min,
        opacity: 0.16 + 0.44 * Math.min(1, demand) });
    }
  }
  return cells.map((cell) => (
    <Rectangle key={cell.id} bounds={cell.bounds} interactive={false}
      pathOptions={{ stroke: false, fillColor: coverageColor(cell.min, threshold), fillOpacity: cell.opacity }} />
  ));
}

// Top historical hot-spots as flame-orange rings so supervisors can position units on demand.
export function HotspotMarkers({ hotspots }) {
  return hotspots.slice(0, 15).map((hs, i) => (
    <CircleMarker key={i} center={[hs.lat, hs.lng]} radius={6 + 8 * hs.weight}
      pathOptions={{ color: "#F97316", weight: 2, fillColor: "#F97316", fillOpacity: 0.25 }}>
      <Tooltip direction="top">{`HOT-SPOT — ${hs.count} calls (90 days)`}</Tooltip>
    </CircleMarker>
  ));
}

// NEW (additive): OPERATIONAL WATCH ZONES — geofence circles from the Command
// Center. Fetched when the layer is toggled on; refreshed every 60 s.
const ZONE_PURPOSE_LABEL = { perimeter: "PERIMETER", school: "SCHOOL ZONE", event: "EVENT",
  hazard: "HAZARD", patrol: "PATROL", restricted: "RESTRICTED" };
export function ZoneLayer() {
  const [zones, setZones] = useState([]);
  useEffect(() => {
    const load = () => client.get("/command/zones").then((r) => setZones(r.data.zones || [])).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);
  return zones.filter((z) => valid(z.center?.lat, z.center?.lng)).map((z) => (
    <Circle key={z.zone_id} center={[z.center.lat, z.center.lng]} radius={z.radius_m}
      pathOptions={{ color: z.color || "#F59E0B", weight: 2, dashArray: "10 6", fillColor: z.color || "#F59E0B", fillOpacity: 0.08 }}>
      <Tooltip direction="center" permanent className="route-eta-tip">
        <span style={{ fontFamily: "monospace", fontSize: 9, fontWeight: 700 }}>
          ⬡ {z.name} · {ZONE_PURPOSE_LABEL[z.purpose] || z.purpose?.toUpperCase()}
        </span>
      </Tooltip>
    </Circle>
  ));
}
